import { Box, Flex, Link } from '@chakra-ui/react';
import { Checkbox, Text } from '@kula-ai/components';
import { fillIdPlaceholders } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { JobCardData, JobListBlockData } from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';

interface JobListBlockProps {
  data: JobListBlockData;
}

// Status → badge colors. Falls back to neutral for unknown/raw labels.
const STATUS_STYLE: Record<
  string,
  { bg: string; color: string; label: string }
> = {
  live: { bg: 'success-50', color: 'success-700', label: 'Live' },
  open: { bg: 'success-50', color: 'success-700', label: 'Open' },
  draft: { bg: 'warning-50', color: 'warning-700', label: 'Draft' },
  unlisted: { bg: 'secondary-100', color: 'secondary-600', label: 'Unlisted' },
  closed: { bg: 'secondary-100', color: 'secondary-600', label: 'Closed' },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const key = status.toLowerCase();
  const s = STATUS_STYLE[key] ?? {
    bg: 'secondary-100',
    color: 'secondary-600',
    label: status,
  };
  return (
    <Flex
      align="center"
      gap="4px"
      px="8px"
      py="2px"
      borderRadius="full"
      bg={s.bg}
      flexShrink={0}
    >
      <Box w="6px" h="6px" borderRadius="full" bg={s.color} />
      <Text
        variant="small"
        className="font-medium"
        style={{ color: `var(--chakra-colors-${s.color})` }}
      >
        {s.label}
      </Text>
    </Flex>
  );
};

// The meta line: department · office · workplace · employment type. Only the
// parts we actually have, joined with dots.
const metaLine = (job: JobCardData): string =>
  [job.department, job.office, job.workplace, job.employmentType]
    .filter(Boolean)
    .join(' · ');

const JobCard = ({
  job,
  checked,
  onToggle,
}: {
  job: JobCardData;
  /** undefined = list is not selectable (single-job confirmation card). */
  checked?: boolean;
  onToggle?: () => void;
}) => {
  const meta = metaLine(job);
  const selectable = typeof checked === 'boolean' && !!onToggle;
  return (
    <Box
      border="1px solid"
      borderColor={checked ? 'primary-300' : 'secondary-200'}
      borderRadius="10px"
      bg="base-white"
      px="14px"
      py="12px"
      _hover={{
        borderColor: checked ? 'primary-300' : 'secondary-300',
        boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
      }}
      transition="all 0.12s"
    >
      <Flex align="center" justify="space-between" gap="10px">
        <Flex align="center" gap="10px" minW="0">
          {selectable && (
            <Checkbox
              size="sm"
              checked={checked}
              onChange={onToggle}
              aria-label={`Select ${job.title}`}
            />
          )}
          <Text variant="span" className="text-secondary-900 font-semibold">
            {job.title}
          </Text>
        </Flex>
        <StatusBadge status={job.status} />
      </Flex>
      {meta && (
        <Text
          variant="small"
          className="text-secondary-500"
          style={{ marginTop: 2 }}
        >
          {meta}
        </Text>
      )}
      {job.url && (
        // Own block line — the meta Text above renders inline (span), so an
        // inline-block link would sit flush against it ("full_timeView job").
        <Box mt="8px">
          <Link
            href={job.url}
            isExternal
            color="primary-500"
            onClick={(e) => e.stopPropagation()}
            display="inline-block"
          >
            <Text variant="small" className="font-medium text-primary-500">
              View job ↗
            </Text>
          </Link>
        </Box>
      )}
    </Box>
  );
};

// A scannable list of job cards. Used for search results, job pickers, and the
// created-job confirmation — one consistent card everywhere a job appears.
// Multi-job lists are SELECTABLE like candidate cards: tick jobs, then the
// chips below act on the selection (falling back to the whole list when
// nothing is ticked). Follow-up chips render below the cards, primary first.
export const JobListBlock = ({ data }: JobListBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const blockKey = data.toolCallId ?? 'no-tool-call-id';
  const selection = useAgentForgeStore((s) => s.selectionByBlock[blockKey]);
  const toggle = useAgentForgeStore((s) => s.toggleBlockSelection);
  if (!data.jobs?.length) return null;

  // Selection UX only makes sense with a real choice — the single-job
  // confirmation card keeps its plain look.
  const selectable = data.jobs.length > 1;
  const selectedIds = selection ?? [];
  const allIds = data.ids ?? data.jobs.map((j) => j.id);
  const titleById = Object.fromEntries(data.jobs.map((j) => [j.id, j.title]));

  const sendFollowup = (template: string) => {
    if (isSending) return;
    // Selection drives the action; with nothing ticked, the whole list.
    const ids = selectedIds.length > 0 ? selectedIds : allIds;
    const names = ids.map((id) => titleById[id] ?? '');
    void send(fillIdPlaceholders(template, ids, undefined, names));
  };

  // Show the narrowed count on multi-target chips once a selection exists,
  // same convention as candidate chips.
  const actions =
    selectedIds.length > 0
      ? (data.suggestedActions ?? []).map((a) =>
          a.promptTemplate.includes('{ids}') ||
          a.promptTemplate.includes('{count}')
            ? { ...a, label: `${a.label} (${selectedIds.length})` }
            : a
        )
      : data.suggestedActions;

  return (
    <Flex direction="column" gap="8px" maxW="520px">
      {data.summary && (
        <Text variant="small" className="text-secondary-500">
          {data.summary}
        </Text>
      )}
      {data.jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          checked={selectable ? selectedIds.includes(job.id) : undefined}
          onToggle={selectable ? () => toggle(blockKey, job.id) : undefined}
        />
      ))}
      {actions && actions.length > 0 && (
        <Box mt="4px">
          <SuggestedActionChips
            actions={actions}
            onSelect={sendFollowup}
            disabled={isSending}
            testIdPrefix="joblist-suggested-action"
          />
        </Box>
      )}
    </Flex>
  );
};
